import React, { useEffect, useState } from "react";
import { api, formatDetail, fmtDateTime } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, EmptyState, StatusBadge } from "@/components/ui-parts";
import { Plus, KeyRound, Copy, Trash2, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import ExternalSources from "@/pages/ExternalSources";

export default function ApiKeys() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [allowedApp, setAllowedApp] = useState("");
  const [newKey, setNewKey] = useState(null); // just-created key
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState({}); // id -> {key, copied, until_ts}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-keys");
      setItems(data);
    } catch { toast.error("Konnte API-Keys nicht laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/api-keys", { name, allowed_app: allowedApp || null });
      setNewKey(data);
      setName(""); setAllowedApp("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`API-Key "${k.name}" widerrufen?`)) return;
    try {
      await api.delete(`/api-keys/${k.id}`);
      toast.success("Key widerrufen");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const revealKey = async (k) => {
    if (revealed[k.id]) {
      // Toggle off
      setRevealed((r) => { const n = { ...r }; delete n[k.id]; return n; });
      return;
    }
    try {
      const { data } = await api.post(`/api-keys/${k.id}/reveal`);
      setRevealed((r) => ({ ...r, [k.id]: { key: data.key, copied: false } }));
      // auto-hide after 30s
      setTimeout(() => {
        setRevealed((r) => { const n = { ...r }; delete n[k.id]; return n; });
      }, 30000);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Anzeigen fehlgeschlagen");
    }
  };

  const copyRevealed = async (k) => {
    const shown = revealed[k.id];
    if (!shown) return;
    await navigator.clipboard.writeText(shown.key);
    setRevealed((r) => ({ ...r, [k.id]: { ...shown, copied: true } }));
    setTimeout(() => {
      setRevealed((r) => r[k.id] ? { ...r, [k.id]: { ...r[k.id], copied: false } } : r);
    }, 2000);
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <PageHeader
        title="API-Zugänge"
        subtitle="Verbinden Sie andere Emergent-Apps (z.B. Eurostay.world, mobigast.de, mont-aro.de) mit vorbuch.de."
        actions={
          !showForm && (
            <PrimaryButton onClick={() => setShowForm(true)} data-testid="add-apikey-btn">
              <Plus className="h-4 w-4" /> Neuer API-Key
            </PrimaryButton>
          )
        }
      />

      <div className="px-8 py-8 space-y-6">
        <ExternalSources />

        <div className="border-t border-[#E6E6E4] pt-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold mb-4">
            <KeyRound className="h-3.5 w-3.5" /> API-Keys
          </div>
        </div>

        {newKey && (
          <div className="bg-[#E4F0E8] border border-[#18322B]/20 rounded-md p-5" data-testid="new-key-panel">
            <div className="font-display font-bold text-base text-[#18322B]">API-Key erstellt</div>
            <div className="text-xs text-[#1E4620] mt-1">Kopieren Sie diesen Schlüssel jetzt – er wird nicht erneut angezeigt.</div>
            <div className="mt-3 flex items-center gap-2 bg-white border border-[#E6E6E4] rounded-md px-3 py-2 font-mono text-xs">
              <span className="flex-1 truncate select-all" data-testid="new-key-value">{newKey.key}</span>
              <button onClick={copyKey}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#18322B] text-white hover:bg-[#21453B] transition-colors duration-150"
                data-testid="copy-new-key">
                {copied ? <><Check className="h-3.5 w-3.5" /> Kopiert</> : <><Copy className="h-3.5 w-3.5" /> Kopieren</>}
              </button>
            </div>
            <div className="mt-4 text-xs text-[#1E4620]">
              <div className="font-semibold mb-1">Beispiel-Verwendung:</div>
              <pre className="bg-white border border-[#E6E6E4] rounded-md p-3 text-[11px] overflow-x-auto font-mono">
{`curl -H "X-API-Key: ${newKey.key}" \\
  ${backendUrl}/api/external/summary`}
              </pre>
            </div>
            <button className="mt-3 text-xs text-[#18322B] underline" onClick={() => setNewKey(null)}>Schließen</button>
          </div>
        )}

        {showForm && (
          <form onSubmit={create} className="bg-white border border-[#E6E6E4] rounded-md p-6 space-y-4" data-testid="apikey-form">
            <div className="font-display font-bold text-lg text-[#1A1A1A]">Neuer API-Key</div>
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Name / Beschreibung</label>
              <input required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="apikey-name-input"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]"
                placeholder="Eurostay Produktions-Integration" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Zugehörige App (optional)</label>
              <select value={allowedApp} onChange={(e) => setAllowedApp(e.target.value)}
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white focus:outline-none focus:border-[#18322B]">
                <option value="">— Beliebige —</option>
                <option value="eurostay.world">Eurostay.world</option>
                <option value="mobigast.de">Mobigast.de</option>
                <option value="mont-aro.de">Mont-aro.de</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <SecondaryButton type="button" onClick={() => setShowForm(false)}>Abbrechen</SecondaryButton>
              <PrimaryButton type="submit" data-testid="apikey-form-save">Key erstellen</PrimaryButton>
            </div>
          </form>
        )}

        <div className="bg-white border border-[#E6E6E4] rounded-md p-5">
          <div className="font-display font-bold text-base text-[#1A1A1A]">Verfügbare Endpunkte</div>
          <div className="mt-3 space-y-2 text-xs font-mono text-[#5C5E60]">
            <div><span className="text-[#1E4620] font-semibold">GET</span> {backendUrl}/api/external/ping</div>
            <div><span className="text-[#1E4620] font-semibold">GET</span> {backendUrl}/api/external/summary</div>
            <div><span className="text-[#1E4620] font-semibold">GET</span> {backendUrl}/api/external/companies</div>
            <div><span className="text-[#915000] font-semibold">POST</span> {backendUrl}/api/external/receipts (multipart · file, company_id?)</div>
            <div><span className="text-[#915000] font-semibold">POST</span> {backendUrl}/api/external/invoices <span className="text-[#1A4971]">(Rechnungsarchiv-Webhook, nur bezahlte Rechnungen)</span></div>
            <div><span className="text-[#1E4620] font-semibold">GET</span> {backendUrl}/api/external/invoices?source_app=eurostay</div>
          </div>
          <div className="mt-3 text-xs text-[#5C5E60]">Header <span className="font-mono bg-[#F3F3F1] px-1.5 py-0.5 rounded">X-API-Key: bp_...</span> ist erforderlich.</div>

          <details className="mt-4 group">
            <summary className="cursor-pointer text-xs font-semibold text-[#18322B] hover:underline">
              Rechnungsarchiv-Webhook · Details & Beispiel
            </summary>
            <div className="mt-3 text-xs text-[#5C5E60] space-y-3">
              <div>
                Sendet <b>nur bezahlte Rechnungen</b> zur zentralen Archivierung. Duplikate (gleicher <code className="bg-[#F3F3F1] px-1 rounded">source_app</code> + <code className="bg-[#F3F3F1] px-1 rounded">external_id</code>) werden erkannt.
              </div>
              <div>
                <b className="text-[#1A1A1A]">Pflichtfelder (multipart/form-data):</b>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  <li><code>file</code> – PDF oder Bild der Rechnung</li>
                  <li><code>source_app</code> – z.B. &bdquo;eurostay&ldquo;, &bdquo;mobigast&ldquo;, &bdquo;mont-aro&ldquo;</li>
                  <li><code>issuing_company</code> – Aussteller, z.B. &bdquo;Goldenbed GmbH&ldquo;</li>
                  <li><code>invoice_number</code>, <code>invoice_date</code> (YYYY-MM-DD), <code>total_amount</code></li>
                  <li><code>payment_status</code> – <b>muss &bdquo;paid&ldquo; oder &bdquo;bezahlt&ldquo; sein</b></li>
                </ul>
              </div>
              <div>
                <b className="text-[#1A1A1A]">Optional:</b> <code>net_amount</code>, <code>tax_amount</code>, <code>tax_rate</code>, <code>currency</code>, <code>customer_reference</code>, <code>paid_at</code>, <code>payment_method</code>, <code>external_id</code>, <code>metadata_json</code>, <code>category</code>, <code>company_id</code>
              </div>
              <pre className="bg-white border border-[#E6E6E4] rounded-md p-3 text-[11px] overflow-x-auto font-mono leading-relaxed">
{`curl -X POST ${backendUrl}/api/external/invoices \\
  -H "X-API-Key: bp_..." \\
  -F "file=@rechnung_2026-0042.pdf" \\
  -F "source_app=eurostay" \\
  -F "issuing_company=Goldenbed GmbH" \\
  -F "invoice_number=2026-0042" \\
  -F "invoice_date=2026-02-15" \\
  -F "total_amount=1249.90" \\
  -F "net_amount=1050.34" \\
  -F "tax_amount=199.56" \\
  -F "tax_rate=19" \\
  -F "currency=EUR" \\
  -F "payment_status=paid" \\
  -F "paid_at=2026-02-20" \\
  -F "payment_method=SEPA" \\
  -F "customer_reference=Kunde Müller" \\
  -F "external_id=eurostay-inv-42" \\
  -F 'metadata_json={"buchung":"5820","kostenstelle":"AT"}'`}
              </pre>
              <div className="text-[#5C5E60]">
                Antwort: gespeicherter Beleg inkl. <code>id</code>, <code>company_id</code> (automatisch zugeordnet), <code>duplicate: true/false</code>.
              </div>
            </div>
          </details>
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : items.length === 0 ? (
          <EmptyState title="Noch keine API-Keys" hint="Erstellen Sie einen Key, um andere Emergent-Apps zu verbinden." />
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="apikeys-list">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="text-left px-6 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">App</th>
                  <th className="text-left px-4 py-3 font-semibold">Key</th>
                  <th className="text-left px-4 py-3 font-semibold">Zuletzt genutzt</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <tr key={k.id} className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8] transition-colors duration-150">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                        <span className="font-medium text-[#1A1A1A]">{k.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5C5E60]">{k.allowed_app || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#5C5E60]">
                      {revealed[k.id] ? (
                        <div className="inline-flex items-center gap-1.5 bg-[#F9F9F8] border border-[#E6E6E4] rounded px-2 py-0.5 max-w-[380px]">
                          <span className="truncate select-all text-[#1A1A1A]" data-testid={`revealed-key-${k.id}`}>
                            {revealed[k.id].key}
                          </span>
                          <button onClick={() => copyRevealed(k)}
                            className="text-[#18322B] hover:text-[#21453B] shrink-0"
                            title="Kopieren">
                            {revealed[k.id].copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          </button>
                        </div>
                      ) : (
                        <span>{k.key_preview}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#5C5E60] text-xs">{fmtDateTime(k.last_used_at)}</td>
                    <td className="px-4 py-3">
                      {k.revoked ? <StatusBadge variant="error">Widerrufen</StatusBadge> : <StatusBadge variant="success">Aktiv</StatusBadge>}
                    </td>
                    <td className="text-right px-6 py-3">
                      {!k.revoked && (
                        <>
                          <button onClick={() => revealKey(k)}
                            data-testid={`reveal-apikey-${k.id}`}
                            title={revealed[k.id] ? "Verbergen" : "Vollständigen Key anzeigen (30 s)"}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#5C5E60] transition-colors duration-150 mr-1">
                            {revealed[k.id] ? <><EyeOff className="h-3.5 w-3.5" strokeWidth={1.75} /> Verbergen</> : <><Eye className="h-3.5 w-3.5" strokeWidth={1.75} /> Anzeigen</>}
                          </button>
                          <button onClick={() => revoke(k)}
                            data-testid={`revoke-apikey-${k.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D] transition-colors duration-150">
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Widerrufen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
